import { createRef } from "react";

import { CalendarDays } from "lucide-react";

import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import DatePickerComponent from "@/design-system/day-picker";
import KarendaaDayPicker from "@/design-system/day-picker";
import { CalendarResources, type ICalendarResources } from "@/design-system/day-picker/calendar.Resources";
import { CalendarResourcesEn } from "@/design-system/day-picker/calendar.Resources.En";
import TelemetryButton from "@/design-system/telemetry-button";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector, useUserAuthenticationContext } from "@/src/contexts/UserAuthenticationContext";
import {
    AccountSettingsResources,
    type IAccountSettingsResources,
} from "@/src/settings/AccountSettings.Resources";
import { AccountSettingsResourcesEn } from "@/src/settings/AccountSettings.Resources.En";
import PasswordSettings from "@/src/settings/PasswordSettings";
import {
    type IPasswordSettingsResources,
    PasswordSettingsResources,
} from "@/src/settings/PasswordSettings.Resources";
import { PasswordSettingsResourcesEn } from "@/src/settings/PasswordSettings.Resources.En";
import { defaultAttributes } from "@Components/Component";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { ErrorContainer } from "@Components/ErrorContainer";
import type { ILink } from "@Components/Link";
import { Link } from "@Components/Link";
import { Loading } from "@Components/Loading";
import { AjaxMethod } from "@Components/Modules/Ajax";
import type { IErrors } from "@Components/Modules/ErrorHandler";
import { ErrorType, clearError } from "@Components/Modules/ErrorHandler";
import { capitalize } from "@Components/Modules/Helpers";
import { isFirstLastNameValid, isNullOrEmpty } from "@Components/Modules/Validations";
import type { ITextbox } from "@Components/Textbox";
import { Textbox } from "@Components/Textbox";
import type { IValidator } from "@Components/Validator";
import type { IRadioButtonList } from "@Layout/RadioButtonList";
import { RadioButtonList } from "@Layout/RadioButtonList";
import * as stylex from "@stylexjs/stylex";

import { colors } from "../../styles/tokens.stylex";
import type { IAccountNumber } from "../common/AccountNumber";
import { AccountNumber } from "../common/AccountNumber";
import type { IConfirmationModal } from "../common/ConfirmationModal";
import ConfirmationModal from "../common/ConfirmationModal";
import type { ICreditCardInfo } from "../common/CreditCardInfo";
import { CreditCardInfo } from "../common/CreditCardInfo";
import type { IInformationModal } from "../common/InformationModal";
import { InformationModal } from "../common/InformationModal";
import type { IInputTracker } from "../telemetry/InputTracker";
import { SetInputTrackerProps, TextboxTracker } from "../telemetry/InputTracker";
import type { IEmailConfirmation } from "./EmailConfirmation";
import { EmailConfirmation, IEmailConfirmationTypes } from "./EmailConfirmation";

import * as portalCss from "../shared/Portal.module.css";
import * as css from "./AccountSettings.module.css";

type UserData = {
    alias?: string;
    gender?: string;
    accountNumber?: string;
    recipient?: string;
    birthdate?: string;
};

export interface IAccountSettings extends IComponentAjax<boolean> {
    userLabel: string;
    userTextbox: IInputTracker<ITextbox>;
    verifiedMessage?: string;
    canVerifyEmailAddress: boolean;
    canChangeEmailAddress: boolean;
    canChangePassword: boolean;
    unpinCreditCardLink?: ILink;
    emailConfirmation: IEmailConfirmation;
    aliasLabel: string;
    aliasTextbox: IInputTracker<ITextbox>;
    birthdate?: string;
    genderLabel: string;
    genderList: IInputTracker<IRadioButtonList>;
    cashbackTitle: string;
    firstLastNameLabel: string;
    firstLastNameTextbox: IInputTracker<ITextbox>;
    accountNumber: IAccountNumber;
    changeCashbackInfoModal: IInformationModal;
    waitingConfirmation: boolean;
    waitingConfirmationText: string;
    creditCard?: ICreditCardInfo;
    unpinCreditCardModal?: IConfirmationModal;
    disclaimerInfo?: string;

    bankConnectionLabel?: string;
    bankConnectionText?: string;

    aliasRequiredError: IValidator;
    aliasInvalidLength: IValidator;
    birthDateInvalidError: IValidator;
    genderRequiredError: IValidator;
    firstLastNameInvalidError: IValidator;
    accountNumberInvalidError: IValidator;
    bankInfoInvalidError: IValidator;
    showSettingsSuccess: (message: string) => void;
    setUsername: (username: string) => void;
    resources: IPasswordSettingsResources;
    resourcesAccSettings: IAccountSettingsResources;
    resourcesCalendar: ICalendarResources;
}

interface IAccountSettingsState extends IComponentAjaxState {
    showChangePassword: boolean;
    showChangeEmailAddress: boolean;
    showVerifyEmailAddress: boolean;
    showChangeCashbackInfo: boolean;
    showChangeCreditCardInfo: boolean;
    alias?: string;
    date?: Date;
    gender: string;
    firstLastName: string;
    accountNumber: string;
    waitingConfirmation: boolean;
    initialValues: UserData;
}

class AccountSettings extends ComponentAjax<boolean, IAccountSettings, IAccountSettingsState> {
    private get renderGenderLabel() {
        const genderList = SetInputTrackerProps<IRadioButtonList>({
            ...this.props.genderList,
            changeTelemetryCustom: {
                eventName: "usprfl-genderEdit",
                actionType: "Undefined",
            },
            properties: {
                ...this.props.genderList.properties,
                onChange: (event) => this.setState((state) => ({ ...state, gender: event.target.value })),
                groupName: this.props.genderList.properties.groupName,
            },
        });

        return (
            <div className={css.settingRow}>
                <span className={css.label} data-testid="prof-pers-gender">
                    {this.props.genderLabel}
                </span>
                <RadioButtonList {...genderList} />
            </div>
        );
    }

    protected get renderFirstLastName() {
        const firstLastNameTextbox: ITextbox = SetInputTrackerProps<ITextbox>({
            ...this.props.firstLastNameTextbox,
            focusTelemetryCustom: {
                eventName: "cshbck-wthdrAddresseeEdit",
                actionType: "Undefined",
            },
            properties: {
                maxLength: 90,
                onChange: (event) => {
                    const firstLastName = capitalize(event.target.value);
                    this.setState((state) => ({ ...state, firstLastName }));
                },
                watermarkText: this.props.firstLastNameTextbox.properties.watermarkText,
                value: this.state.firstLastName,
            },
        });

        return (
            <div className={css.settingRow}>
                <span className={css.label}>{this.props.firstLastNameLabel}</span>
                <div>
                    <Textbox {...firstLastNameTextbox} ref="first-last-name-textbox" />
                    <ErrorContainer {...this.errors.firstLastNameInvalidError.container} />
                    <ErrorContainer {...this.errors.firstLastNameInvalidCharsError.container} />
                </div>
            </div>
        );
    }

    protected get renderDisclaimer() {
        return (
            this.props.disclaimerInfo && (
                <div className={`${css.settingRow} ${css.disclaimerInfo}`}>
                    <span>*</span>
                    <span>{this.props.disclaimerInfo}</span>
                </div>
            )
        );
    }

    protected get renderAccountNumber() {
        return (
            <div className={`${css.settingRow} ${css.accountInfo}`}>
                {!this.props.unpinCreditCardLink && (
                    <>
                        <AccountNumber
                            {...this.props.accountNumber}
                            ref="account-number-textbox"
                            onChange={this.validateAccountNumber}
                        />
                        <ErrorContainer {...this.errors.accountNumberInvalidError.container} />
                        <ErrorContainer {...this.errors.bankInfoInvalidError.container} />
                    </>
                )}
                {this.props.creditCard && (
                    <>
                        <CreditCardInfo {...this.props.creditCard} />
                        {this.props.unpinCreditCardLink && (
                            <Link
                                {...this.props.unpinCreditCardLink}
                                onClick={this.onClickChangeCreditCardInfo}
                            />
                        )}
                    </>
                )}
            </div>
        );
    }

    private get renderDatePicker() {
        return (
            <div className={css.settingRow}>
                <span className={css.label} data-testid="prof-pers-bdate">
                    {this.props.resourcesAccSettings.birthDateLabel}
                </span>
                <div>
                    <DatePickerComponent
                        defaultValue={this.state.date}
                        monthNames={this.props.resourcesCalendar.months}
                        weekDays={this.props.resourcesCalendar.weekdays}
                        onChange={(newPate) => {
                            sendTelemetryEvent({
                                eventName: "usprfl-BirthdayEdit",
                                actionType: "Undefined",
                            });

                            this.setState(() => ({ date: newPate }));
                        }}
                        inputPlaceHolder={this.props.resourcesAccSettings.birthDatePlaceholder}
                        data-testid="prof-pers-bdate-input"
                    >
                        <KarendaaDayPicker.Trigger asChild>
                            <button
                                type="button"
                                aria-label="Open date picker"
                                {...stylex.props(styles.calendarButton)}
                            >
                                <CalendarDays size={16} />
                            </button>
                        </KarendaaDayPicker.Trigger>
                    </DatePickerComponent>
                    <ErrorContainer
                        {...this.errors.birthDateInvalidError.container}
                        dataTestId="prof-pers-bdate-error"
                    />
                </div>
            </div>
        );
    }

    private get renderAliasLabel() {
        const aliasInput: IInputTracker<ITextbox> = {
            ...this.props.aliasTextbox,
            focusTelemetryCustom: {
                eventName: "usprfl-nickEdit",
                actionType: "Undefined",
            },
            properties: {
                onChange: (event) => {
                    this.setState((c) => ({ ...c, alias: event.target.value }));
                },
                value: this.state.alias,
                maxLength: 40,
                dataTestId: "prof-pers-user-nick",
            },
        };

        return (
            <div className={css.settingRow}>
                <span className={css.label} data-testid="prof-pers-nick">
                    {this.props.aliasLabel}
                    <span className={portalCss.required}>*</span>
                </span>
                <div>
                    <TextboxTracker {...aliasInput} ref="alias-box" clientId="alias" />
                    <ErrorContainer
                        {...this.errors.aliasRequiredError.container}
                        dataTestId="prof-pers-user-nick-error"
                    />
                    <ErrorContainer
                        {...this.errors.aliasInvalidError.container}
                        dataTestId="prof-pers-user-nick-error"
                    />
                </div>
            </div>
        );
    }

    private get renderChangeEmailAddressLink() {
        const setShowChangeEmail = () =>
            this.setState((state) => ({ ...state, showChangeEmailAddress: true }));

        return (
            <TelemetryButton
                as="button"
                size="None"
                variant="transparent"
                onClick={setShowChangeEmail}
                eventName="usprfl-emailChange"
                data-testid="prof-pers-change-email"
                styleX={styles.linkWrapper}
            >
                <Typography styleX={styles.linkButton}>
                    {this.props.resourcesAccSettings.changeEmailAddress}
                </Typography>
            </TelemetryButton>
        );
    }

    private get renderVerifyEmailAddressLink() {
        return (
            <div {...stylex.props(styles.linkWrapper)}>
                <Button
                    as="button"
                    variant="transparent"
                    size="None"
                    onClick={this.onClickVerifyEmailAddress}
                    data-testid="prof-pers-verify-email"
                >
                    <Typography styleX={styles.linkButton}>
                        {this.props.resourcesAccSettings.verifyEmailAddress}
                    </Typography>
                </Button>
            </div>
        );
    }

    private get renderUserLabel() {
        return (
            <div className={`${css.settingRow} ${css.user}`}>
                <span className={css.label} data-testid="prof-pers-user">
                    {this.props.userLabel}
                </span>
                {this.renderUserTextbox}

                {this.props.canVerifyEmailAddress && this.renderVerifyEmailAddressLink}
                {this.props.canChangeEmailAddress && this.renderChangeEmailAddressLink}
                {this.props.canChangePassword && (
                    <TelemetryButton
                        as="button"
                        size="None"
                        onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            this.setState((state) => ({ ...state, showChangePassword: true }));
                        }}
                        eventName="usprfl-passwordChange"
                        data-testid="prof-pers-change-pass"
                        styleX={[styles.linkWrapper]}
                    >
                        <Typography styleX={styles.linkButton}>
                            {this.props.resourcesAccSettings.changePassword}
                        </Typography>
                    </TelemetryButton>
                )}
            </div>
        );
    }

    private get renderUserTextbox() {
        const userTextbox: ITextbox = SetInputTrackerProps<ITextbox>({
            ...this.props.userTextbox,
            focusTelemetryCustom: {
                eventName: "usprfl-mailChangeAttempt",
                actionType: "Undefined",
            },
            properties: {
                ...this.props.userTextbox.properties,
                lockToggle: true,
                dataTestId: "prof-pers-user-email",
            },
        });

        return (
            <div>
                <Textbox {...userTextbox} />
                {this.props.verifiedMessage && (
                    <span className={css.verifiedMessage}>{this.props.verifiedMessage}</span>
                )}
            </div>
        );
    }

    private get renderBankConnectionLabel() {
        return (
            <div className={css.bankConnection}>
                <div className={css.label}>{this.props.bankConnectionLabel}</div>
                <div className={css.span}>{this.props.bankConnectionText}</div>
            </div>
        );
    }

    private get renderWaitingConfirmation() {
        if (this.props.waitingConfirmationText && !this.state.waitingConfirmation) {
            return null;
        }

        return (
            <p
                className={css.waitingLabel}
                dangerouslySetInnerHTML={{ __html: this.props.waitingConfirmationText }}
            />
        );
    }

    private get renderChangeEmail() {
        if (!this.state.showChangeEmailAddress) {
            return null;
        }

        const changeEmailComponent: IEmailConfirmation = {
            ...this.props.emailConfirmation,
            emailConfirmationType: IEmailConfirmationTypes.ChangeEmailType,
            navigationModal: {
                ...this.props.emailConfirmation.navigationModal,
                onClose: () => this.onCloseChangeEmail(),
            },
        };

        return <EmailConfirmation {...changeEmailComponent} data-testid="prof-pers-verify-email" />;
    }

    private get renderVerifyEmail() {
        if (!this.state.showVerifyEmailAddress) {
            return null;
        }

        const changeEmailComponent: IEmailConfirmation = {
            ...this.props.emailConfirmation,
            emailConfirmationType: IEmailConfirmationTypes.VerifyEmailType,
            navigationModal: {
                ...this.props.emailConfirmation.navigationModal,
                onClose: () => this.onCloseVerifyEmail(),
            },
        };

        return <EmailConfirmation {...changeEmailComponent} />;
    }

    private get bankConnection() {
        if (this.props.bankConnectionLabel && this.props.bankConnectionText) {
            return this.renderBankConnectionLabel;
        }

        return null;
    }

    public accountNumberInvalid: ErrorContainer | null = null;

    public errors: IErrors = {
        default: { ...this.defaultErrors().default, type: ErrorType.Validator },
        aliasRequiredError: {
            name: "alias-required",
            type: ErrorType.Validator,
            targetRef: "alias-box",
            container: { ref: "alias-required-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.alias) === false) {
                    return true;
                }
                return false;
            },
        },
        aliasInvalidError: {
            name: "alias-invalid",
            type: ErrorType.Validator,
            targetRef: "alias-box",
            container: {
                ref: "alias-invalid-container",
                targetRefObj: createRef<HTMLDivElement>(),
            },
            onValidate: () => {
                const alias = this.state.alias?.trim();
                return alias && /^[a-zA-Z0-9ŚŹśźŁĄŻłążĆĘŃÓćęńó ]{3,40}$/.test(alias);
            },
        },
        birthDateInvalidError: {
            name: "birthDate-invalid",
            type: ErrorType.Validator,
            targetRef: "birthDate",
            container: { ref: "birthDate-invalid-container" },
            onValidate: () => {
                const today = new Date();
                const birthDate = this.state.date;

                if (this.state.date === null || this.state.date === undefined) {
                    return true;
                }

                let age = today.getFullYear() - birthDate.getFullYear();
                const m = today.getMonth() - birthDate.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                    age -= 1;
                }

                return age >= 18;
            },
        },
        firstLastNameInvalidCharsError: {
            name: "firstLastName-invalid-chars",
            type: ErrorType.Validator,
            targetRef: "first-last-name-textbox",
            container: { ref: "firstLastName-invalid-chars-container" },
            onValidate: () => {
                if (
                    this.state.firstLastName &&
                    this.state.firstLastName.length > 0 &&
                    !isFirstLastNameValid(this.state.firstLastName)
                ) {
                    return false;
                }
                return true;
            },
        },
        firstLastNameInvalidError: {
            name: "firstLastName-invalid",
            type: ErrorType.Validator,
            targetRef: "first-last-name-textbox",
            container: { ref: "firstLastName-invalid-container" },
            onValidate: () => {
                if (this.state.showChangeCreditCardInfo) {
                    return true;
                }

                if (!this.state.firstLastName || this.state.firstLastName.length === 0) {
                    if (!this.state.accountNumber || this.state.accountNumber.length === 0) {
                        return true;
                    }
                    return false;
                }

                if (
                    !/^[a-z0-9]+ {1}[a-z0-9]+(?:[ -]{1}[a-z0-9]+)?$/i.test(
                        this.state.firstLastName
                            .toLowerCase()
                            .normalize("NFD")
                            .replace(/[\u0300-\u036f]/g, "")
                            .replace(/\u0142/g, "l"),
                    )
                ) {
                    return false;
                }

                const values = this.state.firstLastName.split(" ").filter((v) => v !== "");

                if (values.length > 1) {
                    return true;
                }
                return this.state.firstLastName.trim().length === 0;
            },
        },
        accountNumberInvalidError: {
            name: "account-number-invalid",
            type: ErrorType.Validator,
            targetRef: "account-number-textbox",
            container: { ref: "account-number-invalid-container" },
            onValidate: () => {
                const { accountNumber } = this.state;
                if (!accountNumber && !this.state.firstLastName) {
                    return true;
                }

                if (accountNumber.length === 0 || accountNumber.length === 26) {
                    if (accountNumber.length === 0 && this.state.firstLastName) {
                        return false;
                    }

                    return true;
                }
                return false;
            },
        },
        bankInfoInvalidError: {
            name: "bank-info-invalid",
            type: ErrorType.Validator,
            targetRef: "account-number-textbox",
            container: { ref: "account-number-invalid-container" },
            onValidate: () => {
                if (this.validBankInfo) {
                    return true;
                }

                return false;
            },
        },
    };

    private validBankInfo: boolean = true;

    private get hasChangeCashback() {
        return (
            this.state.initialValues?.accountNumber !== this.state.accountNumber ||
            this.state.initialValues?.recipient !== this.state.firstLastName
        );
    }

    private initialCashbackInfo: boolean =
        this.props.accountNumber.accountNumberTextbox.properties.value != null;

    constructor(props: IAccountSettings) {
        super(props);

        this.state = {
            isLoading: false,
            showChangePassword: false,
            showChangeEmailAddress: false,
            showVerifyEmailAddress: false,
            showChangeCashbackInfo: false,
            showChangeCreditCardInfo: false,
            alias: props.aliasTextbox.properties.value,
            date: props.birthdate && new Date(props.birthdate),
            gender: props.genderList.properties.initialData,
            firstLastName: capitalize(props.firstLastNameTextbox?.properties?.value) || "",
            accountNumber: props.accountNumber.accountNumberTextbox.properties.value || "",
            waitingConfirmation: props.waitingConfirmation,
            initialValues: null,
        };
    }

    public componentDidMount() {
        sendTelemetryEvent({
            eventName: "usprfl-profile",
            actionType: "View",
        });

        this.setState((state) => ({
            ...state,
            initialValues: this.requestParams(false),
        }));
    }

    public render() {
        const isSaveBtnDisabled = this.getSubmitButtonDisabledStatus();
        return (
            <div {...defaultAttributes(this.props, css.accountSettingsComponent)}>
                {this.renderUserLabel}
                {this.renderAliasLabel}
                {this.renderDatePicker}
                {this.renderGenderLabel}
                <h1 className={portalCss.h3Font}>{this.props.cashbackTitle}</h1>
                {this.renderFirstLastName}
                {this.renderAccountNumber}
                {this.renderDisclaimer}
                {this.renderWaitingConfirmation}
                {this.bankConnection}
                <TelemetryButton
                    as="button"
                    size="M"
                    variant={isSaveBtnDisabled ? "disabled" : "magentaRound"}
                    disabled={isSaveBtnDisabled}
                    onClick={this.accountSettingsButtonClick}
                    eventName="common-save"
                    styleX={styles.saveBtn}
                >
                    {this.props.resourcesAccSettings.saveChanges}
                </TelemetryButton>
                {this.state.showChangeCashbackInfo && (
                    <InformationModal
                        {...this.props.changeCashbackInfoModal}
                        onClose={this.hideChangeCashbackInfo}
                    />
                )}
                {this.state.showChangeCreditCardInfo && this.props.unpinCreditCardModal && (
                    <ConfirmationModal
                        {...{
                            ...this.props.unpinCreditCardModal,
                            cancelButton: {
                                ...this.props.unpinCreditCardModal.cancelButton,
                                onClick: () =>
                                    this.setState((state) => ({ ...state, showChangeCreditCardInfo: false })),
                            },
                            nextButton: {
                                ...this.props.unpinCreditCardModal.nextButton,
                                onClick: this.onUnpinCreditCard,
                            },
                        }}
                    />
                )}
                {this.renderAjaxErrorContainer()}
                {this.state.isLoading && <Loading isModal />}
                {this.state.showChangePassword && <PasswordSettings onClose={this.closeModal} />}
                {this.state.showChangeEmailAddress && this.renderChangeEmail}
                {this.state.showVerifyEmailAddress && this.renderVerifyEmail}
            </div>
        );
    }

    protected ajaxSuccess(response: boolean): void {
        if (response === true) {
            if (this.props.showSettingsSuccess) {
                this.props.showSettingsSuccess(this.props.resources.saveChangesMessage);
            }

            this.props.setUsername(this.state.alias);

            if (this.hasChangeCashback && this.initialCashbackInfo) {
                this.setState((state) => ({
                    ...state,
                    isLoading: false,
                    waitingConfirmation: true,
                    showChangeCashbackInfo: true,
                }));
            }

            this.initialCashbackInfo = true;
        }

        this.setState((state) => ({ ...state, isLoading: false, initialValues: this.requestParams(false) }));
    }

    protected ajaxFail(): boolean {
        this.setState((state) => ({ ...state, isLoading: false }));
        return true;
    }

    private hideChangeCashbackInfo = () =>
        this.setState((state) => ({ ...state, showChangeCashbackInfo: false }));

    private validateAccountNumber = (accountNumberValue: string, validBankInfo: boolean) => {
        this.changeAccountNumber(accountNumberValue, validBankInfo);
    };

    private requestParams = (isPost: boolean) => {
        let birthdate: string = null;

        if (this.state.date) {
            birthdate = new Date(
                this.state.date.getTime() - this.state.date.getTimezoneOffset() * 60000,
            ).toISOString();
        }

        const fromState = this.hasChangeCashback || !isPost;

        const result = {
            alias: this.state.alias,
            gender: this.state.gender,
            accountNumber: fromState ? this.state.accountNumber : null,
            recipient: fromState ? this.state.firstLastName : null,
            birthdate,
        } satisfies UserData;

        return result;
    };

    private accountSettingsButtonClick = () => {
        this.ajax.ajaxAction = "/api/settings/ChangeAccountSettings";

        const params = this.requestParams(true);

        this.ajaxRequest(params, () => this.setState((state) => ({ ...state, isLoading: true })));
    };

    private closeModal = () => this.setState((state) => ({ ...state, showChangePassword: false }));

    private changeAccountNumber(accountNumberValue: string, validBankInfo: boolean) {
        const accountNumber = accountNumberValue.replace(/\s/g, "");
        this.validBankInfo = validBankInfo;

        this.setState((state) => ({ ...state, accountNumber }));

        clearError(this, this.errors.accountNumberInvalidError);
        clearError(this, this.errors.bankInfoInvalidError);

        return false;
    }

    private onCloseChangeEmail() {
        this.setState((state) => ({ ...state, showChangeEmailAddress: false }));
    }

    private onCloseVerifyEmail() {
        this.setState((state) => ({ ...state, showVerifyEmailAddress: false }));
    }

    private onClickVerifyEmailAddress = (ev: React.MouseEvent<HTMLButtonElement>): void => {
        ev.preventDefault();

        this.setState((state) => ({ ...state, showVerifyEmailAddress: true }));
    };

    private onClickChangeCreditCardInfo = (ev: React.MouseEvent<HTMLAnchorElement>): void => {
        ev.preventDefault();

        this.setState((state) => ({ ...state, showChangeCreditCardInfo: true }));
    };

    private getSubmitButtonDisabledStatus: () => boolean = () => {
        const values = this.requestParams(false);
        const { initialValues } = this.state;

        if (!initialValues) {
            return true;
        }

        const keys = Object.keys(values) as Array<keyof UserData>;

        for (let i = 0; i < keys.length; i += 1) {
            const key = keys[i];
            if (values[key] !== initialValues[key]) {
                return false;
            }
        }

        return true;
    };

    private onUnpinCreditCard = (ev: React.MouseEvent<HTMLAnchorElement>): void => {
        ev.preventDefault();

        if (this.ajax) {
            this.ajax.ajaxMethod = AjaxMethod.Post;

            this.ajax.success = (response: boolean) => {
                this.setState(
                    (state) => ({ ...state, showChangeCreditCardInfo: false, isLoading: false }),
                    () => {
                        if (response === true) {
                            document.location.reload();
                        }
                    },
                );
            };

            this.ajax.fail = () => {
                this.setState((state) => ({ ...state, showChangeCreditCardInfo: false, isLoading: false }));
                return false;
            };

            this.ajax.ajaxAction = "/api/settings/BankUnlinking";
            this.ajaxRequest();

            this.setState((state) => ({ ...state, showChangeCreditCardInfo: true, isLoading: true }));
        }
    };
}

const AccountSettingsConnect = (props: IAccountSettings) => {
    const { updateUsername } = useUserAuthenticationContext();
    const resources = languageSelector(PasswordSettingsResources, PasswordSettingsResourcesEn);
    const resourcesAccountSettings = languageSelector(AccountSettingsResources, AccountSettingsResourcesEn);
    const resourcesCalendar = languageSelector(CalendarResources, CalendarResourcesEn);

    return (
        <AccountSettings
            {...props}
            ajaxMethod={AjaxMethod.Post}
            setUsername={updateUsername}
            resources={resources}
            resourcesAccSettings={resourcesAccountSettings}
            resourcesCalendar={resourcesCalendar}
        />
    );
};

const styles = stylex.create({
    linkButton: {
        color: colors.fg_Accent,
        fontWeight: 300,
    },
    linkWrapper: {
        display: "block",
        marginTop: 20,
    },
    saveBtn: {
        width: "100%",
    },
    calendarButton: {
        position: "absolute",

        left: "0.6em",
        top: "0.3em",
    },
});

export { AccountSettingsConnect as AccountSettings };
